import { apiClient } from './client'
import type { ApiResponse, DownloadResult } from './client'
import type {
  EmailBlastViewDto,
  BlastRecipientsPageDto,
  CampaignEmailRollupDto,
} from '@/types/emailBlast'

// PulledAfterRegistrationCount (ORB-409) is a non-nullable int on the API DTO, but normalize it once at
// this boundary so a response from an older backend (deploy skew) can't leak a non-number into the funnel
// arithmetic (`TotalAudience − pulled` → NaN, `pulled > 0` → false) downstream. Coerce any non-finite
// value to 0; every consumer can then treat the field as a real number without re-guarding.
const normalizeBlast = (blast: EmailBlastViewDto): EmailBlastViewDto => ({
  ...blast,
  PulledAfterRegistrationCount: Number.isFinite(blast.PulledAfterRegistrationCount)
    ? blast.PulledAfterRegistrationCount
    : 0,
})

const mapBlast = (res: ApiResponse<EmailBlastViewDto>): ApiResponse<EmailBlastViewDto> =>
  res.data ? { ...res, data: normalizeBlast(res.data) } : res

const mapBlasts = (res: ApiResponse<EmailBlastViewDto[]>): ApiResponse<EmailBlastViewDto[]> =>
  res.data ? { ...res, data: res.data.map(normalizeBlast) } : res

// Workspace-scoped email-blast monitor + controls (ORB-357).
// Resume is valid only from Paused (the API returns 409 otherwise — surfaced via ApiResponse.error/status).
// Cancel is a no-op (200) when the blast is already terminal.
export const emailBlastApi = {
  // List blasts for a campaign, optionally narrowed to one web.
  list: (workspaceId: string, campaignId: string, webId?: string): Promise<ApiResponse<EmailBlastViewDto[]>> => {
    const query = new URLSearchParams({ campaignId })
    if (webId) query.set('webId', webId)
    return apiClient.get<EmailBlastViewDto[]>(`/api/Workspaces/${workspaceId}/Email/Blasts?${query.toString()}`).then(mapBlasts)
  },

  get: (workspaceId: string, id: string): Promise<ApiResponse<EmailBlastViewDto>> =>
    apiClient.get<EmailBlastViewDto>(`/api/Workspaces/${workspaceId}/Email/Blasts/${id}`).then(mapBlast),

  // Resume a Paused blast. 409 if the blast is not Paused.
  resume: (workspaceId: string, id: string): Promise<ApiResponse<EmailBlastViewDto>> =>
    apiClient.post<EmailBlastViewDto>(`/api/Workspaces/${workspaceId}/Email/Blasts/${id}/Resume`, {}).then(mapBlast),

  // Cancel a non-terminal blast. Returns 200 (no-op) when already terminal.
  cancel: (workspaceId: string, id: string): Promise<ApiResponse<EmailBlastViewDto>> =>
    apiClient.post<EmailBlastViewDto>(`/api/Workspaces/${workspaceId}/Email/Blasts/${id}/Cancel`, {}).then(mapBlast),

  // One page of a blast's recipients for the detail drill-down (ORB-384). The API clamps take. Accepts an
  // AbortSignal so rapid paging/search cancels the superseded request (not just its state write).
  recipients: (
    workspaceId: string,
    id: string,
    opts: { skip?: number; take?: number; search?: string; signal?: AbortSignal } = {},
  ): Promise<ApiResponse<BlastRecipientsPageDto>> => {
    const query = new URLSearchParams()
    if (opts.skip != null) query.set('skip', String(opts.skip))
    if (opts.take != null) query.set('take', String(opts.take))
    if (opts.search) query.set('search', opts.search)
    const qs = query.toString()
    return apiClient.get(
      `/api/Workspaces/${workspaceId}/Email/Blasts/${id}/Recipients${qs ? `?${qs}` : ''}`,
      { signal: opts.signal },
    )
  },

  // CSV export of the WHOLE blast's recipient stats (ORB-450). The server streams the file with a
  // Content-Disposition filename; the drill-down's paging/search does not apply. Mirrors websApi.exportAnalytics.
  exportRecipients: (workspaceId: string, blastId: string): Promise<DownloadResult> =>
    apiClient.download(`/api/Workspaces/${encodeURIComponent(workspaceId)}/Email/Blasts/${encodeURIComponent(blastId)}/Recipients/Export`),

  // Campaign-grain email rollup for the campaign Broadcasts tab (ORB-384).
  campaignRollup: (workspaceId: string, campaignId: string, signal?: AbortSignal): Promise<ApiResponse<CampaignEmailRollupDto>> =>
    apiClient.get(`/api/Workspaces/${workspaceId}/Email/Campaigns/${campaignId}/Engagement`, { signal }),
}
